var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DATA_SOURCE_TYPE_DISPLAY_MAP, DataSourceType, } from "healthex-common/datawarehouse.dtos";
import { FormModel } from "./FormModel";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { ParameterizedRemoteData } from "../Infra/RemoteData";
import { UserType } from "healthex-common/user.dtos";
import { assertUserTypeOptional } from "healthex-common/checkusertype";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { EpicEnvironment } from "healthex-common/agentconfig.dtos";
import { ConnectionValidationError } from "healthex-common/smart.dtos";
import { asEnum } from "healthex-common/utils";
import { AxiosError } from "axios";
export const EXTENDED_DATA_SOURCE_TYPE_DISPLAY_MAP = {
    ...DATA_SOURCE_TYPE_DISPLAY_MAP,
    FHIR: "EPIC FHIR",
};
export var DataSourceWizardPage;
(function (DataSourceWizardPage) {
    DataSourceWizardPage["ADD_SOURCE"] = "ADD_SOURCE";
    DataSourceWizardPage["SELECT_DATASET"] = "SELECT_DATASET";
    DataSourceWizardPage["SELECT_TABLES"] = "SELECT_TABLES";
})(DataSourceWizardPage || (DataSourceWizardPage = {}));
export class DataSourceWizardViewModel {
    constructor(opts) {
        this.dataSourceAddDialog = new SimpleDialogModel();
        this.currentPage = DataSourceWizardPage.ADD_SOURCE;
        this.datasetsForDataSource = [];
        this.isEditingDataSource = false;
        this.isLoading = false;
        this.dataSourceWizardErrorMessage = undefined;
        this.dataConnectionValidated = false;
        this.currentDataSourceId = null;
        this.internalEpicClientIds = null;
        this.dataWarehouseFormModel = new FormModel({
            name: "",
            type: DataSourceType.DATAWAREHOUSE_BIGQUERY,
            authConfig: "",
            account: "",
            username: "",
            password: "",
            warehouse: "",
            database: "",
            schema: "",
            agentURL: "",
            fhirURL: "",
            clientId: "",
            clientSecret: "",
            epicEnvironment: EpicEnvironment.PRODUCTION,
        }, ["name", "type", "authConfig"]);
        this.tablesForDataset = [];
        this.selectedTableIds = [];
        this.selectedDataSetId = null;
        this.organizationsManager = opts.container.organizationsManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.provisioningService = opts.container.provisioningService;
        this.invalidationManager = opts.container.invalidationManager;
        this.adminUIManager = opts.container.adminUIManager;
        this.onClose = opts.onClose;
        this.isEditingDataSource = opts.isEditingDataSource;
        this.currentPage = opts.currentPage;
        this.currentDataSourceId = opts.currentDataSourceId;
        this.agentIsHealthExManaged = opts.agentIsHealthExManaged;
        this.organizationData = new ParameterizedRemoteData(this.invalidationManager, () => this.organizationId, () => this.loadOrganization());
        this.dataSourcesData = new ParameterizedRemoteData(this.invalidationManager, () => this.organizationId, async () => {
            return await this.loadDataSources();
        });
        reaction(() => ({
            isEditing: this.isEditingDataSource,
            dataSources: this.dataSources,
            currentId: this.currentDataSourceId,
            selectedType: this.dataWarehouseFormModel.input.type,
        }), async ({ isEditing, dataSources, currentId, selectedType }) => {
            if (currentId === "fhir" || String(selectedType) === "FHIR") {
                this.currentPage = DataSourceWizardPage.ADD_SOURCE;
                await this.loadFhirConfiguration();
                return;
            }
            if (isEditing && dataSources !== NOT_LOADED) {
                const dataSource = dataSources.find((ds) => ds.id === currentId);
                if (dataSource) {
                    if (dataSource.type === DataSourceType.DATAWAREHOUSE_SNOWFLAKE) {
                        const authConfig = JSON.parse(dataSource.authConfig);
                        this.dataWarehouseFormModel.updateInput({
                            type: dataSource.type,
                            name: dataSource.name,
                            authConfig: dataSource.authConfig,
                            account: authConfig.account || "",
                            username: authConfig.username || "",
                            password: authConfig.password || "",
                            warehouse: authConfig.warehouse || "",
                            database: authConfig.database || "",
                            schema: authConfig.schema || "",
                        });
                    }
                    else if (dataSource.type === DataSourceType.DATAWAREHOUSE_BIGQUERY) {
                        this.dataWarehouseFormModel.updateInput({
                            type: dataSource.type,
                            name: dataSource.name,
                            authConfig: dataSource.authConfig,
                        });
                    }
                }
            }
        });
        if (this.currentPage === DataSourceWizardPage.SELECT_DATASET && this.currentDataSourceId) {
            void this.updateDataSetSelection(this.currentDataSourceId);
        }
        else if (this.currentPage === DataSourceWizardPage.SELECT_TABLES && this.currentDataSourceId) {
            void this.updateTablesSelection(this.currentDataSourceId);
        }
    }
    setSelectedTableIds(tableNames) {
        this.selectedTableIds = tableNames;
    }
    setSelectedDataSetId(datasetId) {
        this.selectedDataSetId = datasetId;
    }
    get organization() {
        return this.organizationData.value;
    }
    get enableDataConnectionType() {
        return this.dataConnectionValidated || !this.showDataGatewayURLSection;
    }
    get showDataGatewayURLSection() {
        return !this.agentIsHealthExManaged;
    }
    get showEpicFields() {
        return String(this.dataWarehouseFormModel.input.type) === "FHIR";
    }
    get epicClientIds() {
        if (this.internalEpicClientIds === NOT_LOADED || this.internalEpicClientIds === null)
            return null;
        if (this.dataWarehouseFormModel.input.epicEnvironment === EpicEnvironment.PRODUCTION) {
            return {
                patientAuthenticator: this.internalEpicClientIds.patientAuthenticator.production,
                patientDataConnector: this.internalEpicClientIds.patientDataConnector.production,
            };
        }
        else {
            return {
                patientAuthenticator: this.internalEpicClientIds.patientAuthenticator.nonproduction,
                patientDataConnector: this.internalEpicClientIds.patientDataConnector.nonproduction,
            };
        }
    }
    showCopySuccess(successMessage) {
        this.bannerUIManager.displayBanner({ text: successMessage });
    }
    get epicJWKSUrl() {
        if (this.dataWarehouseFormModel.input.epicEnvironment === EpicEnvironment.PRODUCTION) {
            return `${this.dataWarehouseFormModel.input.agentURL}/epic/production/jwks`;
        }
        else {
            return `${this.dataWarehouseFormModel.input.agentURL}/epic/nonproduction/jwks`;
        }
    }
    get dataSources() {
        return this.dataSourcesData.value;
    }
    get selectedDataSetRowIds() {
        return (this.selectedDataSetId ? [this.selectedDataSetId] : [])
            .map((datasetId) => this.datasetsForDataSource.find((ds) => ds.datasetId === datasetId))
            .filter((dataset) => dataset !== undefined)
            .map((dataset) => `${dataset.projectId}_${dataset.datasetId}`);
    }
    updateSelectedDataSets(newSelectionModel) {
        const selectedDataSets = this.datasetsForDataSource
            .filter((dataset) => newSelectionModel.includes(`${dataset.projectId}_${dataset.datasetId}`))
            .map((dataset) => dataset.datasetId);
        this.setSelectedDataSetId(selectedDataSets[0]);
    }
    get selectedTableRowIds() {
        return this.selectedTableIds
            .map((tableName) => this.tablesForDataset.find((t) => t.name === tableName))
            .filter((table) => table !== undefined)
            .map((table) => `${table.dataSourceId}_${table.name}`);
    }
    updateSelectedTables(newSelectionModel) {
        const selectedTables = this.tablesForDataset
            .filter((table) => newSelectionModel.includes(`${table.dataSourceId}_${table.name}`))
            .map((table) => table.name);
        this.setSelectedTableIds(selectedTables);
    }
    get requiredFields() {
        if (String(this.dataWarehouseFormModel.input.type) === "FHIR") {
            return ["agentURL", "fhirURL", "epicEnvironment", "clientId", "clientSecret"];
        }
        else if (this.dataWarehouseFormModel.input.type === DataSourceType.DATAWAREHOUSE_SNOWFLAKE) {
            return ["name", "type", "account", "username", "password", "warehouse", "database", "schema"];
        }
        else {
            return ["name", "type", "authConfig"];
        }
    }
    get isFormValid() {
        const requiredFields = this.requiredFields;
        return (requiredFields.every((field) => Boolean(this.dataWarehouseFormModel.input[field]) && !this.dataWarehouseFormModel.errors[field]) && !this.dataWarehouseFormModel.hasErrors());
    }
    constructAuthConfig() {
        if (this.dataWarehouseFormModel.input.type === DataSourceType.DATAWAREHOUSE_SNOWFLAKE) {
            const { account, username, password, warehouse, database, schema } = this.dataWarehouseFormModel.input;
            return JSON.stringify({
                account,
                username,
                password,
                warehouse,
                database,
                schema,
            });
        }
        return this.dataWarehouseFormModel.input.authConfig;
    }
    async createDataSource() {
        var _a;
        if (!this.organizationId || !this.dataWarehouseFormModel.input.type)
            return;
        if (!this.isFormValid) {
            return;
        }
        try {
            this.isLoading = true;
            const input = this.dataWarehouseFormModel.input;
            const authConfig = this.constructAuthConfig();
            const newDataSource = await this.provisioningService.createDataSource(this.organizationId, {
                type: input.type,
                name: (_a = input.name) !== null && _a !== void 0 ? _a : "",
                authConfig,
            });
            this.currentDataSourceId = newDataSource.id;
            if (input.type === DataSourceType.DATAWAREHOUSE_BIGQUERY) {
                this.datasetsForDataSource = await this.loadDatasetsForDataSource(newDataSource.id);
                this.currentPage = DataSourceWizardPage.SELECT_DATASET;
            }
            else if (input.type === DataSourceType.DATAWAREHOUSE_SNOWFLAKE) {
                this.tablesForDataset = await this.loadTablesForDatasource(newDataSource.id);
                this.currentPage = DataSourceWizardPage.SELECT_TABLES;
            }
        }
        catch (error) {
            this.dataSourceWizardErrorMessage = "Failed to add data source";
        }
        finally {
            this.isLoading = false;
        }
    }
    async updateDataSource() {
        var _a;
        if (!this.organizationId || !this.dataWarehouseFormModel.input.type)
            return;
        if (!this.isFormValid) {
            return;
        }
        const originalDataSource = this.dataSources === NOT_LOADED ? null : this.dataSources.find((ds) => ds.id === this.currentDataSourceId);
        const authConfig = this.constructAuthConfig();
        const authConfigChanged = (originalDataSource === null || originalDataSource === void 0 ? void 0 : originalDataSource.authConfig) !== authConfig;
        await this.provisioningService.updateDataSource(this.organizationId, this.currentDataSourceId, {
            type: this.dataWarehouseFormModel.input.type,
            name: (_a = this.dataWarehouseFormModel.input.name) !== null && _a !== void 0 ? _a : "",
            authConfig,
        });
        if (authConfigChanged) {
            if (this.dataWarehouseFormModel.input.type === DataSourceType.DATAWAREHOUSE_BIGQUERY) {
                this.datasetsForDataSource = await this.loadDatasetsForDataSource(this.currentDataSourceId);
                this.currentPage = DataSourceWizardPage.SELECT_DATASET;
            }
            else if (this.dataWarehouseFormModel.input.type === DataSourceType.DATAWAREHOUSE_SNOWFLAKE) {
                this.tablesForDataset = await this.loadTablesForDatasource(this.currentDataSourceId);
                this.currentPage = DataSourceWizardPage.SELECT_TABLES;
            }
        }
        else {
            await this.closeWizard();
        }
    }
    async closeWizard() {
        this.dataWarehouseFormModel.updateInput({
            name: "",
            type: DataSourceType.DATAWAREHOUSE_BIGQUERY,
            authConfig: "",
            account: "",
            username: "",
            password: "",
            warehouse: "",
            database: "",
            schema: "",
        });
        this.datasetsForDataSource = [];
        this.tablesForDataset = [];
        this.selectedTableIds = [];
        this.selectedDataSetId = null;
        this.dataSourceWizardErrorMessage = undefined;
        this.onClose();
    }
    get nextButtonEnabled() {
        switch (this.currentPage) {
            case DataSourceWizardPage.ADD_SOURCE:
                return this.isFormValid;
            case DataSourceWizardPage.SELECT_DATASET:
                return this.selectedDataSetRowIds.length > 0;
            case DataSourceWizardPage.SELECT_TABLES:
                return true;
            default:
                return false;
        }
    }
    get nextButtonText() {
        switch (this.currentPage) {
            case DataSourceWizardPage.ADD_SOURCE:
                if (String(this.dataWarehouseFormModel.input.type) === "FHIR") {
                    return "Save";
                }
                return "Next";
            case DataSourceWizardPage.SELECT_DATASET:
                return "Next";
            case DataSourceWizardPage.SELECT_TABLES:
                return "Finish";
            default:
                return "Next";
        }
    }
    get organizationId() {
        return this.adminUIManager.organizationId;
    }
    async goToNextPage() {
        switch (this.currentPage) {
            case DataSourceWizardPage.ADD_SOURCE:
                if (String(this.dataWarehouseFormModel.input.type) === "FHIR") {
                    await this.saveFhirConfiguration();
                }
                else if (this.isEditingDataSource) {
                    await this.updateDataSource();
                }
                else {
                    await this.createDataSource();
                }
                break;
            case DataSourceWizardPage.SELECT_DATASET:
                if (this.selectedDataSetId) {
                    try {
                        this.isLoading = true;
                        await this.registerDatasetForDataSource(this.currentDataSourceId, this.selectedDataSetId);
                        this.tablesForDataset = await this.loadTablesForDatasource(this.currentDataSourceId);
                        this.updateSelectedTables(this.tablesForDataset
                            .filter((table) => table.registered)
                            .map((table) => `${this.currentDataSourceId}_${table.name}`));
                        this.currentPage = DataSourceWizardPage.SELECT_TABLES;
                    }
                    finally {
                        this.isLoading = false;
                    }
                }
                break;
            case DataSourceWizardPage.SELECT_TABLES:
                try {
                    this.isLoading = true;
                    await this.registerTablesForDataSource(this.currentDataSourceId, this.selectedTableIds);
                    await new Promise((resolve) => setTimeout(resolve, 5000));
                    await this.dataSourcesData.waitForReload();
                    await this.closeWizard();
                }
                finally {
                    this.isLoading = false;
                }
                break;
        }
    }
    isNextButtonDisabled() {
        switch (this.currentPage) {
            case DataSourceWizardPage.ADD_SOURCE:
                return !this.isFormValid;
            case DataSourceWizardPage.SELECT_DATASET:
                return this.selectedDataSetRowIds.length === 0;
            case DataSourceWizardPage.SELECT_TABLES:
                return this.selectedTableIds.length === 0;
            default:
                return false;
        }
    }
    async saveFhirConfiguration() {
        var _a;
        try {
            this.isLoading = true;
            await this.provisioningService.updateAgentConfig(this.organizationId, {
                ingestType: "epic",
                fhirEndpoint: this.dataWarehouseFormModel.input.fhirURL,
                epicEnvironment: this.dataWarehouseFormModel.input.epicEnvironment,
                organizationId: this.organizationId,
            });
            await this.closeWizard();
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data.connectionValidationError)) {
                const connectionValidationError = asEnum(ConnectionValidationError)(e.response.data.connectionValidationError);
                if (connectionValidationError === ConnectionValidationError.FAILED_UNREACHABLE) {
                    this.dataWarehouseFormModel.addError("fhirURL", "Unable to communicate with FHIR server. Please check the URL.");
                    return;
                }
                else if (connectionValidationError === ConnectionValidationError.FAILED_AUTHENTICATION) {
                    this.dataWarehouseFormModel.addError("fhirURL", "Unable to authenticate to FHIR server. Please make sure both apps are installed as described below.");
                    return;
                }
            }
        }
        finally {
            this.isLoading = false;
        }
    }
    async loadFhirConfiguration() {
        var _a, _b, _c, _d, _e, _f;
        await this.organizationData.waitForValue();
        if (this.organization === NOT_LOADED)
            return;
        if (this.dataConnectionValidated)
            return;
        if (this.organizationId) {
            try {
                this.isLoading = true;
                const agentConfig = await this.provisioningService.getAgentConfig(this.organizationId);
                const epicClientIds = await this.provisioningService.getEpicClientIds(this.organizationId);
                this.internalEpicClientIds = epicClientIds;
                this.dataWarehouseFormModel.updateInput({
                    type: "FHIR",
                    agentURL: (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.agentBaseUrl) !== null && _b !== void 0 ? _b : "",
                    fhirURL: (_c = agentConfig.fhirEndpoint) !== null && _c !== void 0 ? _c : "",
                    epicEnvironment: (_d = agentConfig.epicEnvironment) !== null && _d !== void 0 ? _d : EpicEnvironment.PRODUCTION,
                    clientId: (_e = epicClientIds.patientAuthenticator.production) !== null && _e !== void 0 ? _e : "",
                    clientSecret: (_f = epicClientIds.patientAuthenticator.production) !== null && _f !== void 0 ? _f : "",
                });
                this.dataConnectionValidated = true;
            }
            catch (error) {
                console.error("Failed to load FHIR configuration", error);
                this.dataSourceWizardErrorMessage = "Failed to load FHIR configuration";
            }
            finally {
                this.isLoading = false;
            }
        }
    }
    async updateDataSetSelection(dataSourceId) {
        this.datasetsForDataSource = await this.loadDatasetsForDataSource(dataSourceId);
        if (this.datasetsForDataSource.length === 1) {
            const dataset = this.datasetsForDataSource[0];
            this.updateSelectedDataSets([`${dataset.projectId}_${dataset.datasetId}`]);
        }
        else {
            this.updateSelectedDataSets([]);
        }
    }
    async updateTablesSelection(dataSourceId) {
        this.tablesForDataset = await this.loadTablesForDatasource(dataSourceId);
        this.updateSelectedTables(this.tablesForDataset.filter((table) => table.registered).map((table) => `${dataSourceId}_${table.name}`));
    }
    async loadOrganization() {
        if (!this.organizationId)
            return null;
        return assertUserTypeOptional(await this.organizationsManager.loadOrganization(this.organizationId), UserType.HEALTHCARE_ORGANIZATION);
    }
    async loadDataSources() {
        if (!this.organizationId)
            return [];
        return await this.provisioningService.getDataSources(this.organizationId);
    }
    async loadDatasetsForDataSource(dataSourceId) {
        if (!this.organizationId)
            return [];
        return await this.provisioningService.getDatasetsForDataSource(this.organizationId, dataSourceId);
    }
    async loadTablesForDatasource(dataSourceId) {
        if (!this.organizationId)
            return [];
        return await this.provisioningService.getTablesOfDataSource(this.organizationId, dataSourceId);
    }
    async registerTablesForDataSource(dataSourceId, tableNames) {
        if (!this.organizationId)
            return;
        await this.provisioningService.registerTablesForDataSource(this.organizationId, dataSourceId, { tableNames });
    }
    async registerDatasetForDataSource(dataSourceId, datasetId) {
        if (!this.organizationId)
            return;
        await this.provisioningService.registerDatasetForBigQueryDataSource(this.organizationId, dataSourceId, datasetId);
    }
}
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "currentPage", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "datasetsForDataSource", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "isEditingDataSource", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "isLoading", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "dataSourceWizardErrorMessage", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "dataConnectionValidated", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "currentDataSourceId", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "internalEpicClientIds", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "dataSourcesData", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "tablesForDataset", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "selectedTableIds", void 0);
__decorate([
    observable
], DataSourceWizardViewModel.prototype, "selectedDataSetId", void 0);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "organization", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "enableDataConnectionType", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "showDataGatewayURLSection", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "showEpicFields", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "epicClientIds", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "dataSources", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "selectedDataSetRowIds", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "selectedTableRowIds", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "requiredFields", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "isFormValid", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "nextButtonEnabled", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "nextButtonText", null);
__decorate([
    computed
], DataSourceWizardViewModel.prototype, "organizationId", null);
